import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Building2,
  Eye,
  LayoutGrid,
  List,
  MapPin,
  RotateCcw,
  Search,
  Table2,
  Trash2,
  TrendingUp,
  X,
} from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { ADMIN_ROLES } from "@/lib/roles";
import type { BusinessListing } from "@/types";
import { api, assetUrl } from "@/lib/api";
import { useToast } from "@/components/ui/toast";
import Loader from "@/components/ui/loader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pagination } from "@/components/ui/pagination";
import {
  EmptyPanel,
  PageHero,
  PagePanel,
  PageShell,
  PanelHeading,
} from "@/components/interior-page";

const PAGE_SIZE = 21;

interface LookupItem { id: number; name: string }

const STATUS_LABELS: Record<number, string> = {
  1: "Draft", 2: "Under Review", 3: "Active",
  4: "Under Offer", 5: "Sold", 6: "Expired", 7: "Withdrawn", 8: "Rejected",
  9: "LOI", 10: "Due Diligence", 11: "Escrow",
};
const STATUS_COLORS: Record<number, string> = {
  1: "bg-slate-100 text-slate-700", 2: "bg-amber-500/15 text-amber-600",
  3: "bg-emerald-500/15 text-emerald-600", 4: "bg-cyan-500/15 text-cyan-600",
  5: "bg-sky-500/15 text-sky-600", 6: "bg-zinc-100 text-zinc-700",
  7: "bg-orange-500/15 text-orange-600", 8: "bg-rose-500/15 text-rose-600",
  9: "bg-purple-500/15 text-purple-600", 10: "bg-indigo-500/15 text-indigo-600",
  11: "bg-teal-500/15 text-teal-600",
};

const fmt = (v: number | null | undefined): string => {
  if (!v) return "—";
  if (v >= 1_000_000_000) return `$${+(v / 1_000_000_000).toFixed(1)}B`;
  if (v >= 1_000_000)     return `$${+(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)         return `$${+(v / 1_000).toFixed(1)}K`;
  return `$${v}`;
};

const fmtDate = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

type ViewMode = "list" | "grid" | "table";

export default function DeletedListingsPage() {
  const [listings, setListings] = useState<BusinessListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [restoringId, setRestoringId] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [categories, setCategories] = useState<LookupItem[]>([]);

  const toast = useToast();
  const { hasRole } = useAuth();
  const { pathname } = useLocation();
  const isAdmin = hasRole(...ADMIN_ROLES);

  useEffect(() => {
    // Retired categories included: their listings still need filtering.
    api.get<LookupItem[]>("/lookups/categories?include_inactive=true").then(setCategories).catch(() => {});
  }, []);

  // Page lives in local state (not the URL): a recycle bin has no "back to
  // listings" deep-link to restore, so there's no need for ?page=N — and keeping
  // it local avoids the setSearchParams pagination reset footgun.
  const fetchDeleted = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("page_size", String(PAGE_SIZE));
      if (appliedSearch) params.set("search", appliedSearch);
      const data = await api.get<{ total_elements: number; data: BusinessListing[] }>(
        `/business-listings/deleted?${params.toString()}`,
      );
      setListings(data.data);
      setTotal(data.total_elements);
    } catch {
      setListings([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, appliedSearch]);

  useEffect(() => {
    fetchDeleted();
  }, [fetchDeleted]);

  const applySearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setAppliedSearch(search.trim());
  };

  const clearSearch = () => {
    setSearch("");
    setPage(1);
    setAppliedSearch("");
  };

  const handleRestore = async (e: React.MouseEvent, id: string, title: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (!confirm(`Restore "${title}"? It will return to your listings with its previous status.`)) return;
    setRestoringId(id);
    try {
      await api.post(`/business-listings/${id}/restore`, {});
      setListings((prev) => prev.filter((l) => l.id !== id));
      setTotal((prev) => Math.max(0, prev - 1));
      toast.success(`"${title}" restored`);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to restore listing");
    } finally {
      setRestoringId(null);
    }
  };

  const catName = (id: number | null) => categories.find((c) => c.id === id)?.name ?? null;
  const locationStr = (l: BusinessListing) =>
    [l.city_name, l.state_name, l.country_name].filter(Boolean).join(", ");

  const ViewToggle = () => (
    <div className="flex items-center gap-1 rounded-xl border border-border/60 bg-white/80 p-1">
      {([
        { mode: "list"  as ViewMode, icon: List },
        { mode: "grid"  as ViewMode, icon: LayoutGrid },
        { mode: "table" as ViewMode, icon: Table2 },
      ] as const).map(({ mode, icon: Icon }) => (
        <button
          key={mode}
          type="button"
          onClick={() => setViewMode(mode)}
          className={`flex h-7 w-7 items-center justify-center rounded-lg transition-colors ${
            viewMode === mode ? "bg-gold-gradient text-primary-foreground shadow-sm" : "text-muted-foreground hover:bg-muted"
          }`}
        >
          <Icon className="h-3.5 w-3.5" />
        </button>
      ))}
    </div>
  );

  // Open a deleted listing's detail page (read-only preview before restoring).
  // The detail endpoint allows privileged viewers to read soft-deleted listings.
  const ViewLink = ({ l, variant }: { l: BusinessListing; variant: "full" | "icon" | "round" }) => {
    const common = { to: `/listings/${l.id}`, state: { from: pathname }, title: "View listing" } as const;
    if (variant === "round") {
      return (
        <Link {...common} className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/80 text-slate-600 backdrop-blur-sm transition-colors hover:bg-slate-100">
          <Eye className="h-3.5 w-3.5" />
        </Link>
      );
    }
    if (variant === "icon") {
      return (
        <Link {...common} className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-600 transition-colors hover:bg-muted">
          <Eye className="h-3.5 w-3.5" />
        </Link>
      );
    }
    return (
      <Link {...common} className="inline-flex items-center gap-1 rounded-full border border-border/70 bg-white/80 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-foreground transition-colors hover:bg-white">
        <Eye className="h-4 w-4" />
        View
      </Link>
    );
  };

  // Restore control, rendered in three forms across the views.
  const RestoreButton = ({ l, variant }: { l: BusinessListing; variant: "full" | "icon" | "round" }) => {
    const busy = restoringId === l.id;
    if (variant === "round") {
      return (
        <button
          onClick={(e) => handleRestore(e, l.id, l.title)}
          disabled={busy}
          title="Restore listing"
          className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/80 text-emerald-600 backdrop-blur-sm transition-colors hover:bg-emerald-500/15 disabled:opacity-50"
        >
          <RotateCcw className="h-3.5 w-3.5" />
        </button>
      );
    }
    if (variant === "icon") {
      return (
        <Button
          variant="ghost"
          size="icon"
          onClick={(e) => handleRestore(e, l.id, l.title)}
          disabled={busy}
          title="Restore listing"
          className="h-7 w-7 text-emerald-600 hover:bg-emerald-500/15"
        >
          <RotateCcw className="h-3.5 w-3.5" />
        </Button>
      );
    }
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={(e) => handleRestore(e, l.id, l.title)}
        disabled={busy}
        className="rounded-full border-emerald-500/30 bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20"
      >
        <RotateCcw className="h-4 w-4" />
        <span className="ml-1 text-xs font-bold uppercase tracking-wide">{busy ? "Restoring…" : "Restore"}</span>
      </Button>
    );
  };

  return (
    <PageShell>
      <PageHero
        eyebrow="Deleted Listings"
        title="Recover listings that were removed from your inventory."
        description="Deleted listings are kept here and hidden from buyers. Restore one to bring it back to your active listings with its previous status."
        actions={
          <Button
            size="lg"
            variant="outline"
            className="rounded-full border-primary/20 bg-white/80 text-foreground hover:bg-white"
            onClick={fetchDeleted}
          >
            Refresh
          </Button>
        }
        aside={
          <>
            <div className="text-[0.72rem] font-bold uppercase tracking-[0.22em] text-primary/70">Recycle bin</div>
            <div className="mt-3 text-3xl font-black tracking-tight text-foreground">{total} deleted</div>
            <p className="mt-2 text-sm leading-7 text-muted-foreground">
              {isAdmin
                ? (hasRole("admin")
                  ? "Every deleted listing across the platform appears here."
                  : "Every deleted listing from your organization appears here.")
                : "Listings you removed are kept here until you restore them."}
            </p>
          </>
        }
      />

      <PagePanel>
        <PanelHeading title="Find a deleted listing" description="Search by title, overview, or category to locate the listing you want to restore." />
        <form onSubmit={applySearch} className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/55" />
            <Input placeholder="Keyword..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
          </div>
          <Button type="submit" className="rounded-full">Search</Button>
        </form>
        {appliedSearch && (
          <div className="mt-3">
            <button type="button" onClick={clearSearch} className="inline-flex items-center gap-1.5 rounded-full bg-destructive/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-destructive hover:bg-destructive/15">
              <X className="h-3.5 w-3.5" />Clear search
            </button>
          </div>
        )}
      </PagePanel>

      <PagePanel>
        <div className="mb-5 flex items-center justify-between">
          <PanelHeading title="Deleted listings" description="Restore any listing to return it to your active inventory." />
          <ViewToggle />
        </div>

        {loading ? (
          <Loader className="min-h-[26vh]" />
        ) : listings.length === 0 ? (
          <EmptyPanel
            icon={Trash2}
            title={appliedSearch ? "No deleted listings match your search" : "Your recycle bin is empty"}
            description={appliedSearch ? "Try a different keyword." : "Listings you delete will appear here, ready to restore."}
          />
        ) : viewMode === "list" ? (
          /* ── LIST VIEW ── */
          <div className="space-y-3">
            {listings.map((l) => (
              <article key={l.id} className="rounded-[1.55rem] border border-border/60 bg-white/72 p-4 shadow-[0_1px_0_hsl(0_0%_100%/0.82)_inset]">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate font-display text-xl font-bold leading-snug text-foreground">{l.title}</h3>
                    {l.reference_code && <p className="font-mono text-[0.65rem] tracking-wide text-muted-foreground">{l.reference_code}</p>}
                    {l.overview && <p className="mt-2 line-clamp-2 text-sm leading-7 text-muted-foreground">{l.overview}</p>}
                    <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                      <span className={`rounded-full px-3 py-1.5 font-bold uppercase tracking-[0.14em] ${STATUS_COLORS[l.listing_status_id] || "bg-slate-100 text-slate-700"}`}>
                        {STATUS_LABELS[l.listing_status_id] || `Status ${l.listing_status_id}`}
                      </span>
                      {l.industry_category_id && (
                        <span className="rounded-full bg-primary/10 px-3 py-1.5 font-bold uppercase tracking-[0.14em] text-primary">
                          {catName(l.industry_category_id) || `Category ${l.industry_category_id}`}
                        </span>
                      )}
                      {locationStr(l) && (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1.5 font-bold uppercase tracking-[0.14em] text-slate-700">
                          <MapPin className="h-3.5 w-3.5" />
                          {locationStr(l)}
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-500/10 px-3 py-1.5 font-bold uppercase tracking-[0.14em] text-rose-600">
                        <Trash2 className="h-3.5 w-3.5" />
                        Deleted {fmtDate(l.updated_at)}
                      </span>
                      {isAdmin && l.agent_name && (
                        <span className="rounded-full bg-primary/10 px-3 py-1.5 font-bold uppercase tracking-[0.14em] text-primary">
                          {l.agent_name}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      {l.asking_price && <div className="text-sm font-extrabold text-emerald-600">{fmt(Number(l.asking_price))}</div>}
                      {l.revenue && <div className="text-xs text-muted-foreground">{fmt(Number(l.revenue))} rev</div>}
                    </div>
                    <ViewLink l={l} variant="full" />
                    <RestoreButton l={l} variant="full" />
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : viewMode === "grid" ? (
          /* ── GRID VIEW ── */
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {listings.map((l) => {
              const imgSrc = l.listing_image_url ? assetUrl(l.listing_image_url) : null;
              const cf = l.asking_price && l.ebitda && Number(l.ebitda) !== 0
                ? (Number(l.asking_price) / Number(l.ebitda)).toFixed(2) + "X"
                : null;
              return (
                <article key={l.id} className="group flex h-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/25 hover:shadow-md">
                  {/* Cover image */}
                  <div className="relative h-44 w-full shrink-0 overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200">
                    {imgSrc ? (
                      <img src={imgSrc} alt={l.title} className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center">
                        <Building2 className="h-12 w-12 text-slate-300" />
                      </div>
                    )}
                    <div className="absolute left-3 top-3">
                      <span className={`rounded-full px-2.5 py-1 text-[0.6rem] font-bold uppercase tracking-wide shadow-sm ${STATUS_COLORS[l.listing_status_id] || "bg-slate-100 text-slate-700"}`}>
                        {STATUS_LABELS[l.listing_status_id] || "Unknown"}
                      </span>
                    </div>
                    <div className="absolute left-3 bottom-3">
                      <span className="inline-flex items-center gap-1 rounded-full bg-rose-500/90 px-2.5 py-1 text-[0.6rem] font-bold uppercase tracking-wide text-white shadow-sm">
                        <Trash2 className="h-3 w-3" />
                        Deleted {fmtDate(l.updated_at)}
                      </span>
                    </div>
                    <div className="absolute right-3 top-3 flex items-center gap-1.5">
                      <ViewLink l={l} variant="round" />
                      <RestoreButton l={l} variant="round" />
                    </div>
                  </div>

                  {/* Body */}
                  <div className="flex flex-1 flex-col gap-3 p-4">
                    <div>
                      <div className="min-h-[4.15rem]">
                        <h3 className="line-clamp-2 font-display text-xl font-bold leading-snug text-foreground">{l.title}</h3>
                      </div>
                      {l.reference_code && <p className="mt-0.5 font-mono text-[0.62rem] tracking-wide text-gray-400">{l.reference_code}</p>}
                      {locationStr(l) && (
                        <div className="mt-1 flex items-center gap-1 text-[0.65rem] text-gray-400">
                          <MapPin className="h-3 w-3" />
                          {locationStr(l)}
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-1.5">
                      {l.asking_price && (
                        <div className="flex items-center justify-between rounded-lg border border-border/50 bg-primary/10 px-3 py-2">
                          <span className="text-[0.6rem] font-bold uppercase tracking-wide text-muted-foreground">Asking</span>
                          <span className="text-xs font-extrabold text-foreground">{fmt(Number(l.asking_price))}</span>
                        </div>
                      )}
                      {l.revenue && (
                        <div className="flex items-center justify-between rounded-lg border border-border/50 bg-primary/10 px-3 py-2">
                          <span className="text-[0.6rem] font-bold uppercase tracking-wide text-muted-foreground">Revenue</span>
                          <span className="text-xs font-extrabold text-foreground">{fmt(Number(l.revenue))}</span>
                        </div>
                      )}
                      {l.ebitda && (
                        <div className="flex items-center justify-between rounded-lg border border-border/50 bg-primary/10 px-3 py-2">
                          <span className="text-[0.6rem] font-bold uppercase tracking-wide text-muted-foreground">EBITDA</span>
                          <span className="text-xs font-extrabold text-foreground">{fmt(Number(l.ebitda))}</span>
                        </div>
                      )}
                      {cf && (
                        <div className="flex items-center justify-between rounded-lg border border-border/50 bg-primary/10 px-3 py-2">
                          <span className="text-[0.6rem] font-bold uppercase tracking-wide text-muted-foreground">C.F. Mult.</span>
                          <span className="text-xs font-extrabold text-foreground">{cf}</span>
                        </div>
                      )}
                    </div>

                    <div className="mt-auto flex items-center justify-between gap-2">
                      {l.industry_category_id ? (
                        <span className="truncate rounded-full bg-primary/10 px-2.5 py-1 text-[0.62rem] font-bold uppercase tracking-wide text-primary">
                          {catName(l.industry_category_id) || `Cat ${l.industry_category_id}`}
                        </span>
                      ) : <span />}
                      <div className="flex shrink-0 items-center gap-2">
                        <ViewLink l={l} variant="full" />
                        <RestoreButton l={l} variant="full" />
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          /* ── TABLE VIEW ── */
          <div className="overflow-x-auto rounded-2xl border border-border/60">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/60 bg-muted/40">
                  {["Title", "Status", "Category", "Asking Price", "Revenue", "Location", "Deleted", ""].map((h) => (
                    <th key={h} className="whitespace-nowrap px-4 py-3 text-left text-[0.65rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40">
                {listings.map((l) => (
                  <tr key={l.id} className="group bg-white transition-colors hover:bg-primary/[0.03]">
                    <td className="max-w-[220px] px-4 py-3 font-semibold text-foreground">
                      <span className="block truncate">{l.title}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2.5 py-1 text-[0.62rem] font-bold uppercase tracking-wide ${STATUS_COLORS[l.listing_status_id] || "bg-slate-100 text-slate-700"}`}>
                        {STATUS_LABELS[l.listing_status_id] || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {catName(l.industry_category_id) || "—"}
                    </td>
                    <td className="px-4 py-3 font-extrabold text-emerald-700">
                      <span className="inline-flex items-center gap-1">
                        <TrendingUp className="h-3.5 w-3.5" />
                        {fmt(l.asking_price ? Number(l.asking_price) : null)}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-bold text-amber-700">{fmt(l.revenue ? Number(l.revenue) : null)}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {locationStr(l) ? (
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="h-3.5 w-3.5" />
                          {locationStr(l)}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-rose-600">{fmtDate(l.updated_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <ViewLink l={l} variant="icon" />
                        <RestoreButton l={l} variant="icon" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Pagination
          page={page}
          totalElements={total}
          pageSize={PAGE_SIZE}
          onChange={setPage}
          className="mt-6"
        />
      </PagePanel>
    </PageShell>
  );
}
